import { bytesEqual, concat, copy, fromBase64Url, fromUtf8, toBase64Url, utf8, } from './bytes.js';
import { sha256 } from './hash.js';
import { COSE_ALG, derToRawEcdsa, importPublicJwk } from './keys.js';
export function parseAuthenticatorData(data) {
    if (data.length < 37)
        throw new Error('authenticatorData too short');
    const flags = data[32];
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return {
        rpIdHash: copy(data.subarray(0, 32)),
        flags: {
            userPresent: (flags & 0x01) !== 0,
            userVerified: (flags & 0x04) !== 0,
            backupEligible: (flags & 0x08) !== 0,
            backedUp: (flags & 0x10) !== 0,
            attestedCredentialData: (flags & 0x40) !== 0,
            extensionData: (flags & 0x80) !== 0,
        },
        signCount: view.getUint32(33, false),
    };
}
function fail(reason, detail) {
    return detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };
}
/**
 * Verify a WebAuthn `navigator.credentials.get()` assertion against a pinned credential.
 * Self-contained (WebCrypto only), so daemons can verify passkey approvals offline.
 */
export async function verifyWebAuthnAssertion(assertion, options) {
    let authDataBytes;
    let clientDataBytes;
    let signature;
    let authData;
    let clientData;
    try {
        authDataBytes = fromBase64Url(assertion.authenticatorData);
        clientDataBytes = fromBase64Url(assertion.clientDataJSON);
        signature = fromBase64Url(assertion.signature);
        authData = parseAuthenticatorData(authDataBytes);
        const parsed = JSON.parse(fromUtf8(clientDataBytes));
        if (typeof parsed !== 'object' ||
            parsed === null ||
            typeof parsed.type !== 'string' ||
            typeof parsed.challenge !== 'string' ||
            typeof parsed.origin !== 'string') {
            return fail('malformed', 'clientDataJSON is missing required members');
        }
        clientData = parsed;
    }
    catch (err) {
        return fail('malformed', err instanceof Error ? err.message : String(err));
    }
    if (clientData.type !== 'webauthn.get')
        return fail('wrong-type', clientData.type);
    if (clientData.challenge !== toBase64Url(options.expectedChallenge)) {
        return fail('challenge-mismatch');
    }
    if (!options.rp.origins.includes(clientData.origin)) {
        return fail('origin-mismatch', clientData.origin);
    }
    if (clientData.crossOrigin === true)
        return fail('cross-origin');
    const expectedRpIdHash = await sha256(utf8(options.rp.rpId));
    if (!bytesEqual(authData.rpIdHash, expectedRpIdHash))
        return fail('rp-id-mismatch');
    if (!authData.flags.userPresent)
        return fail('user-not-present');
    if ((options.requireUserVerification ?? true) && !authData.flags.userVerified) {
        return fail('user-not-verified');
    }
    const signedData = concat(authDataBytes, await sha256(clientDataBytes));
    const valid = await verifySignature(options.credential, signature, signedData);
    if (valid === 'unsupported')
        return fail('unsupported-alg');
    if (!valid)
        return fail('bad-signature');
    const prev = options.previousSignCount;
    if (prev !== undefined && prev > 0 && authData.signCount > 0 && authData.signCount <= prev) {
        return fail('counter-regression', `${authData.signCount} <= ${prev}`);
    }
    return { ok: true, authenticatorData: authData, clientData, signCount: authData.signCount };
}
async function verifySignature(credential, signature, data) {
    const subtle = globalThis.crypto.subtle;
    let key;
    try {
        key = await importPublicJwk(credential.publicKey, credential.alg);
    }
    catch {
        return 'unsupported';
    }
    try {
        switch (credential.alg) {
            case COSE_ALG.ES256: {
                let raw;
                try {
                    raw = derToRawEcdsa(signature);
                }
                catch {
                    return false;
                }
                return await subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, raw, data);
            }
            case COSE_ALG.EdDSA:
            case COSE_ALG.Ed25519:
                return await subtle.verify({ name: 'Ed25519' }, key, signature, data);
            case COSE_ALG.RS256:
                return await subtle.verify({ name: 'RSASSA-PKCS1-v1_5' }, key, signature, data);
            default:
                return 'unsupported';
        }
    }
    catch {
        return false;
    }
}
function toBytes(b) {
    return b instanceof Uint8Array ? b : new Uint8Array(b);
}
/**
 * Serialize the pieces of a browser `PublicKeyCredential` (with an AuthenticatorAssertionResponse)
 * into the JSON form used on the wire.
 */
export function assertionToJSON(parts) {
    const out = {
        credentialId: toBase64Url(toBytes(parts.rawId)),
        authenticatorData: toBase64Url(toBytes(parts.authenticatorData)),
        clientDataJSON: toBase64Url(toBytes(parts.clientDataJSON)),
        signature: toBase64Url(toBytes(parts.signature)),
    };
    if (parts.userHandle)
        out.userHandle = toBase64Url(toBytes(parts.userHandle));
    return out;
}
export function isWebAuthnAssertion(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return (typeof v.credentialId === 'string' &&
        typeof v.authenticatorData === 'string' &&
        typeof v.clientDataJSON === 'string' &&
        typeof v.signature === 'string' &&
        (v.userHandle === undefined || typeof v.userHandle === 'string'));
}
//# sourceMappingURL=webauthn.js.map